import React, { useState, useEffect, useRef } from 'react';
import {
    Table, Button, Card, Typography, Tag, Badge,
    Toast, Notification, Pagination, Modal, Spin
} from '@douyinfe/semi-ui';
import { IconDelete, IconRefresh, IconSend } from '@douyinfe/semi-icons';

const { Title, Text } = Typography;

// 用户数据类型
interface User {
    id: string;
    name: string;
    email: string;
    role: string;
    status: 'active' | 'inactive' | 'pending';
    lastLogin: string;
}

// 模拟API服务
class UserApiService {
    private static users: User[] = [
        { id: '1', name: '张三', email: 'zhangsan@example.com', role: '管理员', status: 'active', lastLogin: '2023-08-15' },
        { id: '2', name: '李四', email: 'lisi@example.com', role: '编辑', status: 'active', lastLogin: '2023-08-14' },
        { id: '3', name: '王五', email: 'wangwu@example.com', role: '用户', status: 'inactive', lastLogin: '2023-08-10' },
        { id: '4', name: '赵六', email: 'zhaoliu@example.com', role: '编辑', status: 'pending', lastLogin: '2023-08-12' },
        { id: '5', name: '钱七', email: 'qianqi@example.com', role: '用户', status: 'active', lastLogin: '2023-08-13' },
        { id: '6', name: '孙八', email: 'sunba@example.com', role: '用户', status: 'inactive', lastLogin: '2023-08-09' },
        { id: '7', name: '周九', email: 'zhoujiu@example.com', role: '管理员', status: 'active', lastLogin: '2023-08-14' },
        { id: '8', name: '吴十', email: 'wushi@example.com', role: '编辑', status: 'pending', lastLogin: '2023-08-11' },
        { id: '9', name: '郑十一', email: 'zhengshiyi@example.com', role: '用户', status: 'active', lastLogin: '2023-08-13' },
        { id: '10', name: '王十二', email: 'wangshier@example.com', role: '用户', status: 'inactive', lastLogin: '2023-08-08' },
    ];

    // 获取用户列表
    static async getUsers(page: number = 1, pageSize: number = 5): Promise<{ data: User[], total: number }> {
        return new Promise(resolve => {
            setTimeout(() => {
                const start = (page - 1) * pageSize;
                const end = start + pageSize;
                resolve({
                    data: this.users.slice(start, end),
                    total: this.users.length
                });
            }, 800); // 模拟网络延迟
        });
    }

    // 删除多个用户
    static async deleteUsers(ids: string[]): Promise<boolean> {
        return new Promise(resolve => {
            setTimeout(() => {
                this.users = this.users.filter(user => !ids.includes(user.id));
                resolve(true);
            }, 1000); // 模拟网络延迟
        });
    }
}

const UserManagementTable = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(5);
    const [confirmVisible, setConfirmVisible] = useState<boolean>(false);
    const [deleting, setDeleting] = useState<boolean>(false);

    // 用于在删除后刷新表格
    const refreshFlag = useRef<number>(0);

    // 加载用户数据
    const loadUsers = async (page: number = currentPage, size: number = pageSize) => {
        setLoading(true);
        try {
            const { data, total } = await UserApiService.getUsers(page, size);
            setUsers(data);
            setTotal(total);
            setLoading(false);
        } catch (error) {
            Notification.error({
                title: '加载失败',
                content: '无法加载用户数据，请稍后重试',
                duration: 3
            });
            setLoading(false);
        }
    };

    // 初始加载数据
    useEffect(() => {
        loadUsers();
    }, []);

    // 刷新表格数据
    const refreshTable = () => {
        // 使用时间戳强制刷新
        //refreshFlag.current = Date.now();
        loadUsers();
        setSelectedRowKeys([]);
    };

    // 删除选中用户
    const deleteSelectedUsers = async () => {
        setDeleting(true);
        try {
            const success = await UserApiService.deleteUsers(selectedRowKeys);
            if (success) {
                Toast.success('删除成功！');
                refreshTable();
            } else {
                Toast.error('删除失败，请重试');
            }
        } catch (error) {
            Toast.error('删除过程中发生错误');
        } finally {
            setDeleting(false);
            setConfirmVisible(false);
        }
    };

    // 渲染状态标签
    const renderStatus = (status: User['status']) => {
        switch (status) {
            case 'active':
                return <Tag color="green">激活</Tag>;
            case 'inactive':
                return <Tag color="red">未激活</Tag>;
            case 'pending':
                return <Tag color="orange">待审核</Tag>;
            default:
                return <Tag>未知</Tag>;
        }
    };

    // 表格列配置
    const columns = [
        {
            title: '姓名',
            dataIndex: 'name',
            width: 150
        },
        {
            title: '邮箱',
            dataIndex: 'email',
            width: 200
        },
        {
            title: '角色',
            dataIndex: 'role',
            render: (role: string) => <Tag>{role}</Tag>
        },
        {
            title: '状态',
            dataIndex: 'status',
            render: renderStatus
        },
        {
            title: '最后登录',
            dataIndex: 'lastLogin',
            width: 120
        }
    ];

    // 行选择配置
    const rowSelection = {
        selectedRowKeys,
        onChange: (selectedRowKeys: any[]) => {
            setSelectedRowKeys(selectedRowKeys as string[]);
        },
        getCheckboxProps: (record: any) => ({
            disabled: record.status === 'pending' // 禁用待审核用户的选择
        })
    };

    // 处理分页变化
    const handlePageChange = (page: number, pageSize?: number) => {
        setCurrentPage(page);
        if (pageSize) setPageSize(pageSize);
        loadUsers(page, pageSize);
        setSelectedRowKeys([]);
    };

    return (
        <div style={{ padding: 20, maxWidth: 1200, margin: '0 auto' }}>
            <Card style={{ marginBottom: 24 }}>
                <Title heading={2} style={{ marginBottom: 16 }}>用户管理系统</Title>
                <Text type="tertiary">选择多个用户后，可以批量删除或执行其他操作</Text>
            </Card>

            <Card
                style={{ marginBottom: 24 }}
                header={(
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Title heading={4}>用户列表</Title>
                        <div>
                            <Badge count={selectedRowKeys.length} overflowCount={99} />
                            <Text type="tertiary" style={{ marginLeft: 8 }}>已选择</Text>
                        </div>
                    </div>
                )}
                footer={(
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Button
                            type="danger"
                            theme="borderless"
                            icon={<IconDelete />}
                            disabled={selectedRowKeys.length === 0}
                            onClick={() => setConfirmVisible(true)}
                        >
                            删除选中 ({selectedRowKeys.length})
                        </Button>

                        <div style={{ display: 'flex', gap: 12 }}>
                            <Button
                                type="secondary"
                                icon={<IconRefresh />}
                                onClick={refreshTable}
                            >
                                刷新表格
                            </Button>
                            <Button
                                type="primary"
                                theme="solid"
                                icon={<IconSend />}
                                onClick={() => {
                                    Toast.info(`已提交 ${selectedRowKeys.length} 个用户的操作`);
                                }}
                                disabled={selectedRowKeys.length === 0}
                            >
                                执行操作
                            </Button>
                        </div>
                    </div>
                )}
            >
                <Spin spinning={loading}>
                    <Table
                        //key={refreshFlag.current} // 强制刷新表格的关键
                        columns={columns}
                        dataSource={users}
                        rowKey="id"
                        //rowSelection={rowSelection}
                        pagination={false}
                        style={{ width: '100%' }}
                    />
                </Spin>

                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 24 }}>
                    <Pagination
                        currentPage={currentPage}
                        pageSize={pageSize}
                        total={total}
                        onChange={handlePageChange}
                        showSizeChanger
                        onPageSizeChange={(size) => {
                            setPageSize(size);
                            setCurrentPage(1);
                            loadUsers(1, size);
                        }}
                    />
                </div>
            </Card>

            <Card style={{ marginTop: 24 }}>
                <Title heading={5} style={{ marginBottom: 16 }}>操作指南</Title>
                <ul style={{ paddingLeft: 20 }}>
                    <li><Text>使用表格左侧复选框选择多个用户</Text></li>
                    <li><Text>选择用户后，顶部会显示已选择的数量</Text></li>
                    <li><Text>点击"删除选中"按钮可以批量删除用户</Text></li>
                    <li><Text>点击"刷新表格"按钮重新加载数据</Text></li>
                    <li><Text>点击"执行操作"按钮对选中用户执行操作</Text></li>
                    <li><Text>待审核用户无法被选择（禁用状态）</Text></li>
                </ul>
            </Card>

            {/* 删除确认对话框 */}
            <Modal
                title="确认删除"
                visible={confirmVisible}
                onCancel={() => setConfirmVisible(false)}
                footer={(
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                        <Button onClick={() => setConfirmVisible(false)}>取消</Button>
                        <Button
                            type="danger"
                            theme="solid"
                            loading={deleting}
                            onClick={deleteSelectedUsers}
                        >
                            确认删除
                        </Button>
                    </div>
                )}
            >
                <div style={{ padding: '20px 10px' }}>
                    <Text>确定要删除选中的 <Text strong>{selectedRowKeys.length}</Text> 个用户吗？</Text>
                    <div style={{ marginTop: 16 }}>
                        <Text type="warning">此操作不可撤销，删除的用户将无法恢复！</Text>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default UserManagementTable;